import React, { useCallback, useState } from "react";
import { FiAlertTriangle, FiCheckCircle } from "react-icons/fi";
import { post as commsPost } from "../../../service/comms/commsApi";
import { bulkTicketAction } from "../../../service/comms/supportService";
import Loader from "../../../common/Loader/Loader";
import { Toaster } from "../../../common/Toaster/Toaster";
import { requestSupportResync } from "../../../hooks/comms/RoomsUnreadProvider";
import { TicketRow } from "./TicketRow";
import { BulkActionBar } from "./BulkActionBar";
import { useSupportTags } from "./useSupportVocabulary";
import { summarizeBulkResults } from "./supportUtils";
import { DEPARTMENTS, PRIORITIES, SORTS, WAITING_ON } from "./supportEnums";

/** Copy for the empty state of each saved view. */
const EMPTY_STATES = {
  red_zone: {
    title: "Nothing has breached",
    body: "Every ticket is inside its reply window. A breach here alerts the owner and their manager.",
  },
  pool: {
    title: "Pool is clear",
    body: "Every incoming ticket has an owner. New ones land here the moment they arrive — you'll get an alert.",
  },
  mine: { title: "Nothing assigned to you", body: "Pick something up from the pool." },
  all_open: { title: "No open tickets", body: "The desk is clear." },
  escalated: { title: "Nothing escalated to you", body: "" },
  waiting: { title: "Nothing waiting on a customer", body: "" },
  snoozed: { title: "Nothing snoozed", body: "" },
  resolved_today: {
    title: "Nothing resolved today yet",
    body: "Earlier ones are under Resolved · all.",
  },
  resolved_all: { title: "No resolved tickets", body: "" },
};

const selectClass =
  "rounded border border-gray-200 px-2 py-1 text-[11px] font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-primary/40";

/**
 * The middle pane: filters, the red-zone banner, the dense ticket list and the
 * bulk bar.
 *
 * Every filter is a server-side predicate — the browser re-deriving `breached`
 * or a view is exactly how a queue starts disagreeing with its own sidebar
 * counts past page one. The one exception is the ops-bar search, which is
 * page-local and says so in the footer.
 *
 * @param {Object} props
 * @param {Object} props.queue - `useSupportQueue` result.
 * @param {{ departments?: Object }} props.counts
 * @param {string|null} props.selectedRoomId
 * @param {(ticket: Object) => void} props.onSelect
 * @param {Array<{ userId: string, name: string }>} props.agents - Bulk assign targets.
 * @param {() => void} props.onChanged - Called after a bulk action lands.
 */
export function TicketList({ queue, counts, selectedRoomId, onSelect, agents, onChanged }) {
  const { tags: tagVocabulary } = useSupportTags();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const breachedCount = counts?.views?.red_zone ?? 0;
  const empty = EMPTY_STATES[queue.view] || { title: "Nothing here", body: "" };

  const handleBulk = useCallback(
    async (payload) => {
      const roomIds = Array.from(queue.selectedIds);
      if (roomIds.length === 0) return;
      setBulkBusy(true);
      try {
        const response = await bulkTicketAction(commsPost, {
          room_ids: roomIds,
          ...payload,
        });
        const { ok, failed, firstError } = summarizeBulkResults(response, roomIds.length);
        // Partial success is the normal case here — reporting a flat "done"
        // would routinely be wrong (a closed ticket in the selection, an id
        // outside the caller's company).
        setToast({
          id: Date.now(),
          type: failed === 0 ? "success" : ok === 0 ? "error" : "success",
          message:
            failed === 0
              ? `${ok} ticket${ok === 1 ? "" : "s"} updated.`
              : `${ok} of ${ok + failed} updated${firstError ? ` — ${firstError}` : ""}.`,
        });
        queue.clearSelection();
        requestSupportResync();
        onChanged();
      } catch (err) {
        setToast({ id: Date.now(), type: "error", message: err?.message || "Bulk action failed." });
      } finally {
        setBulkBusy(false);
      }
    },
    [queue, onChanged]
  );

  const allChecked =
    queue.tickets.length > 0 && queue.tickets.every((t) => queue.selectedIds.has(t.roomId));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Filters */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5 border-b border-gray-200 px-2.5 py-2">
        <select
          value={queue.filters.department}
          onChange={(e) => queue.setFilter("department", e.target.value)}
          aria-label="Department"
          className={`${selectClass} ${
            queue.filters.department ? "border-orange-primary bg-light-orange text-orange-primary" : ""
          }`}
        >
          <option value="">Dept</option>
          {DEPARTMENTS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          value={queue.filters.priority}
          onChange={(e) => queue.setFilter("priority", e.target.value)}
          aria-label="Priority"
          className={`${selectClass} ${
            queue.filters.priority ? "border-orange-primary bg-light-orange text-orange-primary" : ""
          }`}
        >
          <option value="">Priority</option>
          {PRIORITIES.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>

        <select
          value={queue.filters.tag}
          onChange={(e) => queue.setFilter("tag", e.target.value)}
          aria-label="Tag"
          className={`${selectClass} ${
            queue.filters.tag ? "border-orange-primary bg-light-orange text-orange-primary" : ""
          }`}
        >
          <option value="">Tag</option>
          {tagVocabulary.map((tag) => (
            <option key={tag.name} value={tag.name}>
              {tag.name}
            </option>
          ))}
        </select>

        <select
          value={queue.filters.waiting_on}
          onChange={(e) => queue.setFilter("waiting_on", e.target.value)}
          aria-label="Waiting on"
          className={`hidden ${selectClass} lg:block ${
            queue.filters.waiting_on ? "border-orange-primary bg-light-orange text-orange-primary" : ""
          }`}
        >
          <option value="">Waiting on</option>
          {WAITING_ON.map((w) => (
            <option key={w.key} value={w.key}>
              {w.label}
            </option>
          ))}
        </select>

        <select
          value={queue.sort}
          onChange={(e) => queue.setSort(e.target.value)}
          aria-label="Sort"
          className="ml-auto border-0 bg-transparent text-[11px] text-gray-500 focus:outline-none"
        >
          {SORTS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Red zone banner — only when there is something in it and you aren't
          already looking at it. */}
      {breachedCount > 0 && queue.view !== "red_zone" && (
        <div className="flex flex-shrink-0 items-center gap-2 bg-danger px-2.5 py-1.5">
          <FiAlertTriangle aria-hidden="true" size={14} className="shrink-0 text-white" />
          <span className="flex-1 text-[11px] font-bold text-white">
            RED ZONE · {breachedCount} ticket{breachedCount === 1 ? "" : "s"} past their
            reply deadline · owner + manager alerted
          </span>
          <button
            type="button"
            onClick={() => queue.setView("red_zone")}
            className="text-[11px] font-bold text-white underline"
          >
            Show only these
          </button>
        </div>
      )}

      {/* Column header */}
      <div className="flex flex-shrink-0 items-center gap-1.5 border-b border-gray-200 border-l-[3px] border-l-transparent bg-rail-bg px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wider text-gray-400">
        <input
          type="checkbox"
          checked={allChecked}
          onChange={queue.toggleSelectAll}
          aria-label="Select every ticket on this page"
          className="h-3.5 w-3.5 flex-shrink-0 accent-orange-primary"
        />
        <span className="w-11 flex-shrink-0">Id</span>
        <span className="min-w-0 flex-1">Subject</span>
        <span className="hidden w-20 flex-shrink-0 xl:block">Requester</span>
        <span className="hidden w-[70px] flex-shrink-0 lg:block">Dept</span>
        <span className="hidden w-[72px] flex-shrink-0 2xl:block">Type</span>
        <span className="hidden w-[50px] flex-shrink-0 xl:block">Pri</span>
        <span className="w-[86px] flex-shrink-0 text-center">SLA</span>
        <span className="w-10 flex-shrink-0 text-right">Owner</span>
      </div>

      {/* Rows */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {queue.loading ? (
          <Loader />
        ) : queue.error ? (
          <p className="px-3 py-8 text-center text-xs text-danger-dark">{queue.error}</p>
        ) : queue.tickets.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <FiCheckCircle aria-hidden="true" size={28} className="text-gray-300" />
            <p className="m-0 text-sm font-semibold text-gray-700">
              {queue.searching ? "Nothing on this page matches" : empty.title}
            </p>
            {(queue.searching || empty.body) && (
              <p className="m-0 max-w-xs text-xs text-gray-500">
                {queue.searching
                  ? "Search only covers the tickets already loaded — try a view or filter instead."
                  : empty.body}
              </p>
            )}
            {!queue.searching && queue.view !== "mine" && (
              <button
                type="button"
                onClick={() => queue.setView("mine")}
                className="mt-1 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                View my tickets
              </button>
            )}
          </div>
        ) : (
          queue.tickets.map((ticket) => (
            <TicketRow
              key={ticket.roomId}
              ticket={ticket}
              selected={String(ticket.roomId) === String(selectedRoomId)}
              checked={queue.selectedIds.has(ticket.roomId)}
              onToggle={queue.toggleSelect}
              onSelect={onSelect}
              dense={!selectedRoomId}
            />
          ))
        )}
      </div>

      {/* Search is page-local — say so rather than letting a short list read as
          "no such ticket exists". */}
      {queue.searching && queue.tickets.length > 0 && (
        <p className="flex-shrink-0 border-t border-gray-100 px-2.5 py-1 text-[10px] text-gray-400">
          {queue.tickets.length} of {queue.loadedCount} loaded tickets match — search
          covers this page only.
        </p>
      )}

      <BulkActionBar
        selectedCount={queue.selectedIds.size}
        agents={agents}
        busy={bulkBusy}
        onApply={handleBulk}
        onClear={queue.clearSelection}
      />

      {toast && <Toaster key={toast.id} message={toast.message} type={toast.type} />}
    </div>
  );
}

export default TicketList;
